const ACCESS_KEY = 'pulse.shield.access'
const REFRESH_KEY = 'pulse.shield.refresh'
const ACCESS_EXPIRES_AT_KEY = 'pulse.shield.access_expires_at'
const ACCESS_REFRESH_SKEW_MS = 60_000

export type ShieldTokenPair = {
  accessToken: string
  refreshToken: string | null
  accessExpiresAtMs?: number | null
}

function read(key: string): string | null {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value == null) sessionStorage.removeItem(key)
    else sessionStorage.setItem(key, value)
  } catch {
    // private mode / quota: treat as signed out
  }
}

export function getShieldAccessToken(): string | null {
  return read(ACCESS_KEY)
}

export function getShieldRefreshToken(): string | null {
  return read(REFRESH_KEY)
}

export function getShieldAccessExpiresAtMs(): number | null {
  const stored = read(ACCESS_EXPIRES_AT_KEY)
  if (stored) {
    const parsed = Number(stored)
    if (Number.isFinite(parsed) && parsed > 0) return parsed
  }
  const access = getShieldAccessToken()
  return access ? expiresAtFromJwt(access) : null
}

export function hasFreshShieldAccessToken(nowMs: number = Date.now()): boolean {
  const access = getShieldAccessToken()
  if (!access) return false
  const expiresAtMs = getShieldAccessExpiresAtMs()
  if (expiresAtMs == null) return true
  return expiresAtMs - nowMs > ACCESS_REFRESH_SKEW_MS
}

export function storeShieldTokens(pair: ShieldTokenPair): void {
  write(ACCESS_KEY, pair.accessToken)
  write(REFRESH_KEY, pair.refreshToken)
  const expiresAtMs = pair.accessExpiresAtMs ?? expiresAtFromJwt(pair.accessToken)
  write(ACCESS_EXPIRES_AT_KEY, expiresAtMs == null ? null : String(expiresAtMs))
}

export function clearShieldTokens(): void {
  write(ACCESS_KEY, null)
  write(REFRESH_KEY, null)
  write(ACCESS_EXPIRES_AT_KEY, null)
}

function expiresAtFromJwt(token: string): number | null {
  const payload = token.split('.')[1]
  if (!payload) return null
  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
    const body = JSON.parse(globalThis.atob(padded)) as { exp?: unknown }
    return typeof body.exp === 'number' && Number.isFinite(body.exp) ? body.exp * 1000 : null
  } catch {
    return null
  }
}
